/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { ApplicationNotificationUrgency } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ApplicationNotificationProps {
  id: string;
  urgency: ApplicationNotificationUrgency;
  displayOnBanner: boolean;
  dismissable: boolean;
  title: string | null;
  notification: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  createdById: string | null;
  companyId: string | null;
  regionId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ApplicationNotificationRecord extends Record<ApplicationNotificationProps>({
  id: null as any,
  urgency: null as any,
  displayOnBanner: null as any,
  dismissable: null as any,
  title: null as any,
  notification: null as any,
  effectiveDate: null as any,
  expirationDate: null as any,
  createdById: null as any,
  companyId: null as any,
  regionId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    urgency: ApplicationNotificationUrgency;
    displayOnBanner: boolean;
    dismissable: boolean;
    title: string | null;
    notification: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    createdById: string | null;
    companyId: string | null;
    regionId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ApplicationNotificationRecord {
    if (json == null) {
      throw new Error('ApplicationNotificationRecord.fromJS: parameter cannot be null');
    }
    return new ApplicationNotificationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ApplicationNotificationJson.id',
      ),
      urgency: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'urgency', ApplicationNotificationUrgency.Information),
        'ApplicationNotificationJson.urgency',
      ),
      displayOnBanner: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'displayOnBanner', false),
        'ApplicationNotificationJson.displayOnBanner',
      ),
      dismissable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'dismissable', false),
        'ApplicationNotificationJson.dismissable',
      ),
      title: json.title,
      notification: getFromOrThrow(
        json,
        (j) => j.notification,
        'ApplicationNotificationJson.notification',
      ),
      effectiveDate: json.effectiveDate != null ? new Date(json.effectiveDate) : null,
      expirationDate: json.expirationDate != null ? new Date(json.expirationDate) : null,
      createdById: json.createdById,
      companyId: json.companyId,
      regionId: json.regionId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    urgency: ApplicationNotificationUrgency;
    displayOnBanner: boolean;
    dismissable: boolean;
    title: string | null;
    notification: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    createdById: string | null;
    companyId: string | null;
    regionId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ApplicationNotificationRecord, Error[]] {
    if (json == null) {
      throw new Error('ApplicationNotificationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ApplicationNotificationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ApplicationNotificationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      urgency: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'urgency', ApplicationNotificationUrgency.Information),
            'ApplicationNotificationJson.urgency',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      displayOnBanner: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'displayOnBanner', false),
            'ApplicationNotificationJson.displayOnBanner',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dismissable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'dismissable', false),
            'ApplicationNotificationJson.dismissable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title: tryOrDefault(
        () => json.title,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notification: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.notification, 'ApplicationNotificationJson.notification'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      effectiveDate: tryOrDefault(
        () => (json.effectiveDate != null ? new Date(json.effectiveDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      expirationDate: tryOrDefault(
        () => (json.expirationDate != null ? new Date(json.expirationDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ApplicationNotificationProps) {
    super(props);
    if (props == null) {
      throw new Error('ApplicationNotificationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ApplicationNotificationViewModelBase implements ApplicationNotificationProps {
  protected readonly _props: ApplicationNotificationProps;
  private _json?: ApplicationNotificationProps;

  get id(): string {
    return this._props.id;
  }
  get urgency(): ApplicationNotificationUrgency {
    return this._props.urgency;
  }
  get displayOnBanner(): boolean {
    return this._props.displayOnBanner;
  }
  get dismissable(): boolean {
    return this._props.dismissable;
  }
  get title(): string | null {
    return this._props.title;
  }
  get notification(): string {
    return this._props.notification;
  }
  get effectiveDate(): Date | null {
    return this._props.effectiveDate;
  }
  get expirationDate(): Date | null {
    return this._props.expirationDate;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCritical() {
    return this._props.urgency === ApplicationNotificationUrgency.Critical;
  }
  public get isInformation() {
    return this._props.urgency === ApplicationNotificationUrgency.Information;
  }
  public get isWarning() {
    return this._props.urgency === ApplicationNotificationUrgency.Warning;
  }

  public isAnyUrgency(...applicationNotificationUrgency: ApplicationNotificationUrgency[]) {
    return applicationNotificationUrgency.includes(this._props.urgency);
  }

  constructor(props: ApplicationNotificationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        urgency: this._props.urgency,
        displayOnBanner: this._props.displayOnBanner,
        dismissable: this._props.dismissable,
        title: this._props.title,
        notification: this._props.notification,
        effectiveDate: this._props.effectiveDate,
        expirationDate: this._props.expirationDate,
        createdById: this._props.createdById,
        companyId: this._props.companyId,
        regionId: this._props.regionId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllApplicationNotificationEntities = (state: AppState) =>
  state.entityStores.applicationNotification.entities;

export const selectApplicationNotificationById = (id: string) => (state: AppState) =>
  state.entityStores.applicationNotification.entities.get(id);

export const selectApplicationNotificationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.applicationNotification.meta.get(id);

export const selectApplicationNotificationAndMetadataById = (id: string) =>
  createSelector(
    selectApplicationNotificationById(id),
    selectApplicationNotificationMetadataById(id),
    (applicationNotification, meta) => ({ applicationNotification, meta }),
  );

export function selectApplicationNotificationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllApplicationNotificationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ApplicationNotificationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectApplicationNotificationEntitiesByUrgency(
  urgency: ApplicationNotificationUrgency,
) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.urgency === urgency).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByDisplayOnBanner(displayOnBanner: boolean) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.displayOnBanner === displayOnBanner).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByDismissable(dismissable: boolean) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.dismissable === dismissable).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByTitle(title: string) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.title === title).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByNotification(notification: string) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.notification === notification).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByEffectiveDate(effectiveDate: Date) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.effectiveDate === effectiveDate).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByExpirationDate(expirationDate: Date) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.expirationDate === expirationDate).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}

export function selectApplicationNotificationEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllApplicationNotificationEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}
//#endregion Selectors
